import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import { ErrorFallback } from './components/ErrorFallback';
import { getT } from './i18n';
import { resolveClientMarket } from './market/marketProfile';
import './index.css';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { installSimpleAnalyticsEventQueue } from './lib/conversionTelemetry';

initSentry();
if (import.meta.env.VITE_SIMPLE_ANALYTICS === 'true') {
  installSimpleAnalyticsEventQueue();
}

// Same resolution App uses (baked <html data-market> wins over URL/hostname),
// so the fallback locale can never disagree with the rendered app's market.
const market = resolveClientMarket(import.meta.env.VITE_MARKET_DEFAULT);
const t = getT(market);

const rootElement = document.getElementById('root')!;

/**
 * In production the prerender pipeline bakes real HTML into #root.
 * We hydrate (attach event listeners to existing DOM) instead of
 * rendering from scratch to avoid a flash-of-empty-content.
 *
 * In dev, #root is empty so we must use createRoot.
 *
 * The error boundary contributes no DOM of its own, so wrapping App
 * in it is hydration-safe against markup prerendered without it.
 */
const app = (
  <StrictMode>
    <SentryErrorBoundary
      fallback={
        <ErrorFallback
          title={t.errorBoundary_title}
          body={t.errorBoundary_body}
          action={t.errorBoundary_action}
        />
      }
    >
      <App />
    </SentryErrorBoundary>
  </StrictMode>
);

const hasPrerenderedMarkup =
  Array.from(rootElement.childNodes).some(
    (node) =>
      node.nodeType === Node.ELEMENT_NODE ||
      (node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim())),
  );

if (import.meta.env.DEV || !hasPrerenderedMarkup) {
  createRoot(rootElement).render(app);
} else {
  hydrateRoot(rootElement, app);
}
